import { useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/features/authentication/components/auth-provider.tsx'
import {
  getGetUserNotificationsQueryKey,
  useGetUserNotifications,
  useMarkAsRead,
  useTestSyncNotification,
} from '@/utils/api/membership-service'
import { useQueryClient } from '@tanstack/react-query'
import {
  IconBellRinging,
  IconCheck,
  IconCloudDownload,
  IconClock,
  IconDots,
  IconInbox,
  IconRefresh,
  IconUser,
} from '@tabler/icons-react'
import type {
  InternalNotification,
  InternalNotificationFeedResponse,
} from '@/utils/api/membership-service/models'

export function NotificationsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [markingId, setMarkingId] = useState<number | null>(null)

  const userId = user?.id

  const notificationsQuery = useGetUserNotifications(userId ?? 0, {
    query: {
      enabled: typeof userId === 'number',
      staleTime: 30_000,
      refetchInterval: 60_000,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
    },
  })

  const markAsReadMutation = useMarkAsRead()
  const syncNotificationMutation = useTestSyncNotification()

  const notificationsPayload = notificationsQuery.data?.data as
    | InternalNotificationFeedResponse
    | InternalNotification[]
    | undefined

  const notifications = useMemo(() => {
    if (Array.isArray(notificationsPayload)) return notificationsPayload
    if (notificationsPayload && Array.isArray(notificationsPayload.notifications)) {
      return notificationsPayload.notifications
    }
    return []
  }, [notificationsPayload])

  const derivedNotifications = useMemo(() => {
    const mapped = notifications.map((item, index) => {
      const rawId = typeof item.id === 'number' ? item.id : index
      const titleValue = item.title ?? 'Notification'
      const messageValue = item.message ?? ''
      const timestampValue = item.createdAt ?? ''

      return {
        id: rawId,
        isRead: item.isRead === true,
        title: titleValue,
        message: messageValue,
        timestamp: timestampValue,
        category: item.type,
        raw: item,
      }
    })

    return mapped.sort((a, b) => {
      const first = Date.parse(a.timestamp || '')
      const second = Date.parse(b.timestamp || '')
      if (Number.isNaN(first) || Number.isNaN(second)) return 0
      return second - first
    })
  }, [notifications])

  const unreadCount =
    (Array.isArray(notificationsPayload)
      ? notificationsPayload.filter((item) => item.isRead !== true).length
      : notificationsPayload && typeof notificationsPayload.unreadCount === 'number'
        ? notificationsPayload.unreadCount
        : derivedNotifications.filter((item) => !item.isRead).length)
  const lastUpdatedLabel = notificationsQuery.dataUpdatedAt
    ? formatDistanceToNow(notificationsQuery.dataUpdatedAt, { addSuffix: true })
    : 'Not fetched yet'

  const handleRefetch = async () => {
    await notificationsQuery.refetch()
  }

  const handleMarkAsRead = async (notificationId?: number) => {
    if (typeof notificationId !== 'number' || !userId) {
      toast.error('No notification id found to mark as read.')
      return
    }

    setMarkingId(notificationId)
    try {
      await markAsReadMutation.mutateAsync({ notificationId })
      toast.success('Marked as read')
      await queryClient.invalidateQueries({ queryKey: getGetUserNotificationsQueryKey(userId) })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to mark as read right now.'
      toast.error(message)
    } finally {
      setMarkingId(null)
    }
  }

  const handleSync = async () => {
    if (!userId) {
      toast.error('We could not determine your account to sync notifications.')
      return
    }

    try {
      await syncNotificationMutation.mutateAsync({ userId })
      toast.success('Synced new notifications')
      await queryClient.invalidateQueries({ queryKey: getGetUserNotificationsQueryKey(userId) })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to sync new notifications.'
      toast.error(message)
    }
  }

  const renderListContent = () => {
    if (notificationsQuery.isPending) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Fetching your notifications…
        </div>
      )
    }

    if (notificationsQuery.isError) {
      const errorMessage =
        notificationsQuery.error instanceof Error
          ? notificationsQuery.error.message
          : 'We could not load your notifications.'

      return (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive flex flex-col gap-2 rounded-md border p-4"
        >
          <p className="font-semibold">Unable to load notifications</p>
          <p className="text-sm">{errorMessage}</p>
          <Button variant="outline" size="sm" onClick={handleRefetch} className="w-fit">
            <IconRefresh className="mr-2 size-4" />
            Try again
          </Button>
        </div>
      )
    }

    if (!derivedNotifications.length) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/40 px-6 py-10 text-center">
          <IconInbox className="size-7 text-muted-foreground" />
          <p className="text-sm font-medium">No notifications yet</p>
          <p className="text-sm text-muted-foreground">
            When new updates arrive, they will appear here.
          </p>
        </div>
      )
    }

    return (
      <ScrollArea className="max-h-[70vh] pr-2">
        <div className="flex flex-col divide-y">
          {derivedNotifications.map((notification) => {
            const isMarking = markingId === notification.id && markAsReadMutation.isPending
            const parsedDate = notification.timestamp ? new Date(notification.timestamp) : null
            const dateLabel =
              parsedDate && !Number.isNaN(parsedDate.valueOf())
                ? formatDistanceToNow(parsedDate, { addSuffix: true })
                : 'Date unknown'

            return (
              <div key={notification.id} className="flex flex-col gap-3 py-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1 rounded-full bg-primary/10 p-2 text-primary">
                    <IconBellRinging className="size-4" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold leading-tight">{notification.title}</p>
                      {notification.category && (
                        <Badge variant="outline" className="border-primary/40 text-primary">
                          {notification.category.replace(/_/g, ' ')}
                        </Badge>
                      )}
                      <Badge variant={notification.isRead ? 'secondary' : 'default'} className="text-xs">
                        {notification.isRead ? 'Read' : 'Unread'}
                      </Badge>
                    </div>
                    {notification.message && (
                      <p className="text-sm text-muted-foreground">{notification.message}</p>
                    )}
                    {!notification.message && (
                      <p className="text-sm text-muted-foreground">
                        No message provided for this notification.
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <IconClock className="size-3.5" />
                        {dateLabel}
                      </span>
                      <Separator orientation="vertical" className="h-4" />
                      <span className="inline-flex items-center gap-1">
                        <IconUser className="size-3.5" />
                        For you
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMarkAsRead(notification.id)}
                      disabled={notification.isRead || isMarking || !userId}
                    >
                      {isMarking ? (
                        <Loader2 className="mr-2 size-4 animate-spin" />
                      ) : (
                        <IconCheck className="mr-2 size-4" />
                      )}
                      Mark as read
                    </Button>
                    <Button size="icon" variant="ghost" className="text-muted-foreground" disabled>
                      <IconDots className="size-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </ScrollArea>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold leading-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground">
            Review new activity and mark items as read to keep your inbox tidy.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefetch}
            disabled={notificationsQuery.isPending}
          >
            <IconRefresh className="mr-2 size-4" />
            Refresh
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSync}
            disabled={syncNotificationMutation.isPending || !userId}
          >
            {syncNotificationMutation.isPending ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <IconCloudDownload className="mr-2 size-4" />
            )}
            Sync new
          </Button>
        </div>
      </div>

      <Card className="border-muted">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="space-y-1">
            <CardTitle className="text-lg font-semibold">Inbox overview</CardTitle>
            <CardDescription>Stay up to date with membership alerts.</CardDescription>
          </div>
          <div className="rounded-full bg-primary/10 p-2 text-primary">
            <IconBellRinging className="size-5" />
          </div>
        </CardHeader>
        <CardContent className="grid gap-6 sm:grid-cols-3">
          <div className="space-y-1 rounded-lg border bg-card px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total</p>
            <p className="text-2xl font-semibold">{derivedNotifications.length}</p>
          </div>
          <div className="space-y-1 rounded-lg border bg-card px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Unread</p>
            <p className="text-2xl font-semibold">{unreadCount}</p>
          </div>
          <div className="space-y-1 rounded-lg border bg-card px-4 py-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Last updated</p>
            <p className="text-base font-semibold">{lastUpdatedLabel}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-muted">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">All notifications</CardTitle>
          <CardDescription>
            Newest updates appear first. Mark items as read to clear them from your queue.
          </CardDescription>
        </CardHeader>
        <CardContent>{renderListContent()}</CardContent>
      </Card>
    </div>
  )
}
